/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { inject, Injectable } from '@angular/core';
import { AlfrescoApiService } from '@alfresco/adf-content-services';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { processKey, toBpmn, validateGraph } from './flow-graph';
import { FlowDeployment, WorkflowTemplate } from './workflow.service';

/** Where the repository looks for process definitions to deploy. */
const DEFINITIONS_FOLDER = 'Data Dictionary/Workflow Definitions';

/**
 * Publishes a drawn flow to the repository's process engine.
 *
 * Measured on the deployed server: a `bpm:workflowDefinition` node created
 * empty, given its BPMN and then switched on (`bpm:definitionDeployed`) is
 * deployed; writing new BPMN into it later deploys the next version, and
 * processes already running stay on the version they started with. Switching
 * it on before the content exists fails inside the repository, hence the order.
 *
 * The definition node is never deleted from here: deleting it undeploys every
 * version, and with them any process still running.
 */
@Injectable({ providedIn: 'root' })
export class FlowDeployService {
  private readonly api = inject(AlfrescoApiService);

  private core(path: string, method: string, body?: any, query?: any, contentType = 'application/json'): Observable<any> {
    const inst: any = this.api.getInstance();
    return from(inst.contentClient.callApi(path, method, {}, query ?? {}, {}, {}, body ?? null, [contentType], ['application/json']) as Promise<any>);
  }

  private wf(path: string, query?: any): Observable<any> {
    const inst: any = this.api.getInstance();
    return from(
      inst.discoveryClient.callApi(
        `/-default-/public/workflow/versions/1${path}`,
        'GET',
        {},
        query ?? {},
        {},
        {},
        null,
        ['application/json'],
        ['application/json']
      ) as Promise<any>
    );
  }

  /** Deploys the template's graph and answers what was deployed. Refuses a graph with problems. */
  publish(tpl: WorkflowTemplate, by = ''): Observable<FlowDeployment> {
    if (!tpl.graph) {
      return throwError(() => new Error('The template has no graph to publish.'));
    }
    if (validateGraph(tpl.graph).length) {
      return throwError(() => new Error('The graph has problems; fix them before publishing.'));
    }
    const key = processKey(tpl.id);
    const bpmn = toBpmn(tpl.graph, key, tpl.name);
    const name = `${key}.bpmn20.xml`;

    return this.core('/nodes/-root-', 'GET', null, { relativePath: `${DEFINITIONS_FOLDER}/${name}` }).pipe(
      map((res: any) => res.entry.id as string),
      catchError(() =>
        // the first publish: the node, empty and switched off
        this.core('/nodes/-root-', 'GET', null, { relativePath: DEFINITIONS_FOLDER }).pipe(
          switchMap((folder: any) =>
            this.core(`/nodes/${folder.entry.id}/children`, 'POST', {
              name,
              nodeType: 'bpm:workflowDefinition',
              properties: { 'bpm:engineId': 'activiti', 'cm:title': tpl.name }
            })
          ),
          map((res: any) => res.entry.id as string)
        )
      ),
      // the BPMN, then the switch — writing content into a switched-on node is itself a new deployment
      switchMap((nodeId) => this.core(`/nodes/${nodeId}/content`, 'PUT', bpmn, null, 'text/xml').pipe(map(() => nodeId))),
      switchMap((nodeId) => this.core(`/nodes/${nodeId}`, 'PUT', { properties: { 'bpm:definitionDeployed': true } }).pipe(map(() => nodeId))),
      switchMap((nodeId) =>
        this.latestDefinition(key).pipe(
          map(
            (definitionId): FlowDeployment => ({
              key,
              nodeId,
              definitionId,
              templateVersion: tpl.version ?? 1,
              deployedAt: new Date().toISOString(),
              ...(by ? { deployedBy: by } : {})
            })
          )
        )
      )
    );
  }

  /** The newest deployed version of a key, as the engine names it — `key:version:id`. */
  latestDefinition(key: string): Observable<string> {
    return this.wf('/process-definitions', { maxItems: 500 }).pipe(
      map((res: any) => {
        const versions = (res?.list?.entries ?? [])
          .map((e: any) => e.entry)
          .filter((d: any) => d.key === key)
          .sort((a: any, b: any) => (b.version ?? 0) - (a.version ?? 0));
        if (!versions.length) {
          throw new Error('The repository did not deploy the flow.');
        }
        return versions[0].id as string;
      })
    );
  }

  /** Whether the engine still has the deployed key — false when someone removed it by hand. */
  isDeployed(key: string): Observable<boolean> {
    return this.latestDefinition(key).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }
}
